import { useState, useRef, useMemo, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { supabase } from '../supabaseClient';
import type { CurrentUser } from '../types/currentUser';
import type { ShowToast, ShowConfirm, FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// useScheduleController — نفس فكرة باقي الـ controllers، لصفحة "Project
// Schedule". أكثر صفحة معقّدة لحد دلوقتي لأنها متشابكة مع state صفحات تانية:
//
// - phases/setPhases, milestones/setMilestones اتسابوا في App.jsx لأنهم
//   بيتحمّلوا من fetchData المركزية بس، وبرضو phases مُستخدمة في مكان تاني
//   (OverviewView تقريبًا) غير ScheduleView.
// - dependencies/setDependencies, baselineRevisions/setBaselineRevisions
//   اتسابوا في App.jsx لأنهم بيتحمّلوا من fetchData *و* بيتم تعديلهم محليًا
//   مباشرة (addDependency/deleteDependency/createBaselineRevision) من غير
//   ما يعيدوا fetchData — فبيتبعتوا هنا كـ setters.
// - ganttView/setGanttView اتسابوا في App.jsx لأنهم مشتركين كمان مع
//   TimelineView (صفحة Timeline مختلفة تمامًا عن Project Schedule).
// - selectedRevision/setSelectedRevision اتسابوا في App.jsx لأن المودال اللي
//   بيعرضهم (تفاصيل الـ revision) مرسوم في App.jsx نفسها مباشرة، مش جوه
//   ScheduleView.
//
// كل باقي state/handlers الخاصة فعليًا بصفحة Schedule (الفورمات/المودالات/
// فلاتر الـ Gantt/الـ dependencies) بقوا هنا.
// ══════════════════════════════════════════════
export interface Phase {
    id: string | number;
    name: string;
    system_name: string;
    baseline_start: string | null;
    baseline_end: string | null;
    color: string;
    is_critical: boolean;
    order_index: number;
    assigned_members: string[];
    equipment: string[];
    progress?: number;
    baseline_revision_id?: string | number | null;
    baseline_locked?: boolean;
}

// فقط الحقول اللي useScheduleController نفسها بتستخدمها من كل task — الشكل
// الكامل لـ Task لسه معرّف جوه useTasksController (لسه .js) نفسها.
export interface ScheduleTaskLike {
    system?: string;
    progress?: number;
    start_date?: string | null;
    deadline_date?: string | null;
    status?: string;
    // ✅ لازمين لحساب BAC/PV/EV على مستوى الفيز (نفس منطق EVM بتاع
    // App.jsx evmData)، مش بس للـ progress البسيط.
    task_value?: number | string | null;
    baseline_start?: string | null;
    baseline_end?: string | null;
}

export interface Milestone {
    id: string | number;
    name: string;
    planned_date: string;
    actual_date: string | null;
    status: string;
    color: string;
}

export interface Dependency {
    id: string | number;
    from_phase_id: number;
    to_phase_id: string | number;
    type: string;
}

export interface BaselineRevision {
    id: string | number;
    revision_number: number;
    revision_label: string;
    reason: string;
    status: string;
    created_by?: string;
    phases_snapshot?: any[];
}

interface PhaseForm {
    name: string; start_date: string; end_date: string; system_name: string;
    baseline_start: string; baseline_end: string; assigned_members: string[];
    equipment: string[]; progress: number; color: string;
    is_critical: boolean; order_index: number;
}

interface MilestoneForm {
    name: string; planned_date: string; actual_date: string;
    status: string; color: string;
}

interface UseScheduleControllerParams {
    currentProjectId: string | null;
    currentUser: CurrentUser | null;
    showToast: ShowToast;
    showConfirm: ShowConfirm;
    fetchData: FetchData;
    phases: Phase[];
    tasksData: ScheduleTaskLike[];
    dependencies: Dependency[];
    setDependencies: Dispatch<SetStateAction<Dependency[]>>;
    baselineRevisions: BaselineRevision[];
    setBaselineRevisions: Dispatch<SetStateAction<BaselineRevision[]>>;
}

export function useScheduleController({
    currentProjectId, currentUser, showToast, showConfirm, fetchData,
    phases, tasksData, dependencies, setDependencies, baselineRevisions, setBaselineRevisions,
}: UseScheduleControllerParams) {
    const [scheduleView, setScheduleView] = useState('gantt'); // 'gantt' | 'scurve'
    const ganttScheduleRef = useRef<any>(null);
    const [ganttKey, setGanttKey] = useState(0);

    useEffect(() => {
        if (ganttScheduleRef.current) {
            setGanttKey(k => k + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phases, dependencies]);

    const [selectedPhase, setSelectedPhase] = useState<Phase | null>(null);

    const [showAddPhaseModal, setShowAddPhaseModal] = useState(false);
    const [editingPhase, setEditingPhase] = useState<Phase | null>(null);
    const [phaseForm, setPhaseForm] = useState<PhaseForm>({
        name: '', start_date: '', end_date: '', system_name: '',
        baseline_start: '', baseline_end: '', assigned_members: [],
        equipment: [],
        progress: 0, color: '#3b82f6',
        is_critical: false, order_index: 0
    });

    const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
    const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
    const [milestoneForm, setMilestoneForm] = useState<MilestoneForm>({
        name: '', planned_date: '', actual_date: '',
        status: 'Pending', color: '#8b5cf6'
    });

    const [showAddDependency, setShowAddDependency] = useState(false);
    const [depFromPhase, setDepFromPhase] = useState('');
    const [depType, setDepType] = useState('FS');

    const [showCreateRevisionModal, setShowCreateRevisionModal] = useState(false);
    const [revisionForm, setRevisionForm] = useState({ reason: '' });

    // ✅ Progress/Health/SPI محسوبين تلقائيًا من المهام الفعلية لكل phase —
    // دلوقتي بمنطق EVM حقيقي (Roll-up) بدل متوسط بسيط: الفيز (= السيستم)
    // بتاخد BAC/PV/EV كـ SUM لتاسكاتها، بنفس معادلة evmData بتاع App.jsx
    // (plannedPct من task.baseline_start/baseline_end)، عشان الـ progress%
    // والـ SPI على مستوى الفيز يبقوا متّسقين وقابلين للمقارنة المباشرة مع
    // أرقام EVM على مستوى المشروع كله — مش حساب مستقل بمنطق مختلف.
    // ملحوظة: AC/CPI مش محسوبين هنا لأن budget_expenses مرتبطة بـ category
    // بس، مفيش ربط فعلي بـ system/phase في قاعدة البيانات يسمح بتوزيع
    // المصروفات على الأنظمة.
    const phasesFromTasks = useMemo(() => {
        const today = new Date();

        const plannedPct = (t: ScheduleTaskLike) => {
            if (!t.baseline_start || !t.baseline_end) return null;
            const bs = new Date(t.baseline_start);
            const be = new Date(t.baseline_end);
            if (isNaN(bs.getTime()) || isNaN(be.getTime()) || be <= bs) return null;
            if (today <= bs) return 0;
            if (today >= be) return 100;
            return ((today.getTime() - bs.getTime()) / (be.getTime() - bs.getTime())) * 100;
        };

        return phases.map(phase => {
            const systemTasks = tasksData.filter(t => t.system === phase.system_name);

            let bac = 0, pv = 0, ev = 0;
            let missingValueCount = 0, missingBaselineCount = 0, valuedTasksCount = 0;
            systemTasks.forEach(t => {
                const val = parseFloat(String(t.task_value)) || 0;
                if (val <= 0) { missingValueCount++; return; } // تاسك من غير قيمة = مستبعد من حساب الفيز بالقيمة (زي EVM بالظبط)
                valuedTasksCount++;
                bac += val;
                ev += val * ((t.progress || 0) / 100);
                const pct = plannedPct(t);
                if (pct === null) missingBaselineCount++;
                else pv += val * (pct / 100);
            });

            // ✅ فيز لسه مفيش فيها تاسكات بقيمة مُدخلة — نرجع للمتوسط البسيط
            // القديم بدل ما نطلع progress=0 بالغلط (data quality، مش bug).
            const valueWeighted = bac > 0;
            const progress = valueWeighted
                ? Math.round((ev / bac) * 100)
                : (systemTasks.length
                    ? Math.round(systemTasks.reduce((s, t) => s + (t.progress || 0), 0) / systemTasks.length)
                    : (phase.progress || 0));

            const startDates = systemTasks.map(t => t.start_date).filter((d): d is string => Boolean(d));
            const endDates = systemTasks.map(t => t.deadline_date).filter((d): d is string => Boolean(d));
            const actualStart = startDates.length ? startDates.reduce((a, b) => a < b ? a : b) : null;
            const actualEnd = endDates.length ? endDates.reduce((a, b) => a > b ? a : b) : null;

            let plannedProgress = 0;
            if (valueWeighted) {
                // ✅ متّسق مع EVM: PV/BAC لنفس تاسكات السيستم دا، بدل نسبة
                // زمنية من baseline الفيز نفسه.
                plannedProgress = Math.round((pv / bac) * 100);
            } else if (phase.baseline_start && phase.baseline_end) {
                const total = new Date(phase.baseline_end).getTime() - new Date(phase.baseline_start).getTime();
                const elapsed = today.getTime() - new Date(phase.baseline_start).getTime();
                plannedProgress = total > 0 ? Math.min(Math.max(Math.round((elapsed / total) * 100), 0), 100) : 0;
            }

            // SPI = EV/PV (مباشر من القيم، مش من نسبتين مقرّبتين) — نفس
            // صيغة evmData بالحرف.
            const spi = pv > 0 ? Number((ev / pv).toFixed(2)) : null;

            let variance: number | null = null;
            if (phase.baseline_end && actualEnd) {
                variance = Math.round((new Date(actualEnd).getTime() - new Date(phase.baseline_end).getTime()) / 86400000);
            }

            let health = 'On Track';
            if (progress < plannedProgress - 20) health = 'Delayed';
            else if (progress < plannedProgress - 10) health = 'At Risk';

            const completed = systemTasks.filter(t => t.status === 'Completed').length;
            const overdue = systemTasks.filter(t =>
                t.deadline_date && new Date(t.deadline_date) < today && t.status !== 'Completed'
            ).length;

            return {
                ...phase,
                progress,
                plannedProgress,
                spi,
                variance,
                health,
                start_date: actualStart,
                end_date: actualEnd,
                taskCount: systemTasks.length,
                completed,
                overdue,
                // ✅ جديد — أرقام EVM الحقيقية بالريال على مستوى الفيز (Roll-up
                // من task_value بتاع تاسكات السيستم دا بس، مفيش عمود مخزّن جديد).
                bac, pv, ev,
                valueWeighted,
                missingValueCount,
                missingBaselineCount,
                valuedTasksCount,
            };
        });
    }, [phases, tasksData]);

    const addPhase = async () => {
        await supabase.from('project_phases').insert([{
            project_id: currentProjectId,
            name: phaseForm.name,
            system_name: phaseForm.system_name,
            baseline_start: phaseForm.baseline_start,
            baseline_end: phaseForm.baseline_end,
            color: phaseForm.color,
            is_critical: phaseForm.is_critical,
            order_index: phases.length,
            assigned_members: phaseForm.assigned_members || [],
            equipment: phaseForm.equipment || [],
        }]);
        showToast('✅ Phase added!');
        setShowAddPhaseModal(false);
        setPhaseForm({ name: '', start_date: '', end_date: '', system_name: '', baseline_start: '', baseline_end: '', progress: 0, color: '#3b82f6', is_critical: false, order_index: 0, assigned_members: [], equipment: [] });
        fetchData(currentProjectId);
    };

    const updatePhase = async () => {
        if (!editingPhase) return;
        await supabase.from('project_phases').update({
            name: editingPhase.name,
            system_name: editingPhase.system_name,
            baseline_start: editingPhase.baseline_start,
            baseline_end: editingPhase.baseline_end,
            color: editingPhase.color,
            is_critical: editingPhase.is_critical,
            assigned_members: editingPhase.assigned_members || [],
            equipment: editingPhase.equipment || [],
        }).eq('id', editingPhase.id);
        showToast('✅ Phase updated!');
        setEditingPhase(null);
        fetchData(currentProjectId);
    };

    const deletePhase = async (id: string | number) => {
        showConfirm('Are you sure you want to delete this phase?', async () => {
            await supabase.from('project_phases').delete().eq('id', id);
            showToast('Phase deleted');
            fetchData(currentProjectId);
        })
    };

    const addMilestone = async () => {
        await supabase.from('project_milestones').insert([{
            project_id: currentProjectId,
            ...milestoneForm,
        }]);
        showToast('✅ Milestone added!');
        setShowAddMilestoneModal(false);
        setMilestoneForm({ name: '', planned_date: '', actual_date: '', status: 'Pending', color: '#8b5cf6' });
        fetchData(currentProjectId);
    };

    const updateMilestone = async () => {
        if (!editingMilestone) return;
        await supabase.from('project_milestones').update({
            name: editingMilestone.name,
            planned_date: editingMilestone.planned_date,
            actual_date: editingMilestone.actual_date,
            status: editingMilestone.status,
            color: editingMilestone.color,
        }).eq('id', editingMilestone.id);
        showToast('✅ Milestone updated!');
        setEditingMilestone(null);
        fetchData(currentProjectId);
    };

    const deleteMilestone = async (id: string | number) => {
        showConfirm('Are you sure you want to delete this milestone?', async () => {
            await supabase.from('project_milestones').delete().eq('id', id);
            showToast('Milestone deleted');
            fetchData(currentProjectId);
        }
        )
    };

    const addDependency = async () => {
        if (!depFromPhase || !selectedPhase) return;
        const { data } = await supabase.from('phase_dependencies').insert({
            project_id: currentProjectId,
            from_phase_id: parseInt(depFromPhase),
            to_phase_id: selectedPhase.id,
            type: depType,
        }).select().single();

        if (data) {
            setDependencies(prev => [...prev, data]);
            setShowAddDependency(false);
            setDepFromPhase('');
        }
    };

    const deleteDependency = async (depId: string | number) => {
        await supabase.from('phase_dependencies').delete().eq('id', depId);
        setDependencies(prev => prev.filter(d => d.id !== depId));
    };

    const createBaselineRevision = async () => {
        const revNum = baselineRevisions.length;

        const { data: revision, error } = await supabase
            .from('baseline_revisions')
            .insert({
                project_id: currentProjectId,
                revision_number: revNum,
                revision_label: `Rev ${revNum}`,
                reason: revisionForm.reason,
                status: 'Pending',
                created_by: currentUser?.name,
                phases_snapshot: phases.map(p => ({ // ✅ احفظ snapshot
                    id: p.id,
                    name: p.name,
                    color: p.color,
                    baseline_start: p.baseline_start,
                    baseline_end: p.baseline_end,
                })),
            })
            .select().single();

        if (error) { console.error('Baseline save error:', error); showToast('❌ Failed to save baseline. Please try again.'); return; }

        // اقفل الـ phases بالـ revision id
        // اقفل الـ phases لما تبعت للعميل
        await supabase.from('project_phases')
            .update({
                baseline_revision_id: revision.id,
                baseline_locked: true, // مقفول فوراً لما تبعت للعميل
            })
            .eq('project_id', currentProjectId);

        setBaselineRevisions(prev => [revision, ...prev]);
        setShowCreateRevisionModal(false);
        setRevisionForm({ reason: '' });
        showToast('✅ Baseline revision created! Awaiting client approval.');
    };

    return {
        scheduleView, setScheduleView, ganttKey, ganttScheduleRef,
        selectedPhase, setSelectedPhase,
        showAddPhaseModal, setShowAddPhaseModal, editingPhase, setEditingPhase, phaseForm, setPhaseForm, addPhase, updatePhase, deletePhase,
        showAddMilestoneModal, setShowAddMilestoneModal, editingMilestone, setEditingMilestone, milestoneForm, setMilestoneForm, addMilestone, updateMilestone, deleteMilestone,
        showAddDependency, setShowAddDependency, depFromPhase, setDepFromPhase, depType, setDepType, addDependency, deleteDependency,
        showCreateRevisionModal, setShowCreateRevisionModal, revisionForm, setRevisionForm, createBaselineRevision,
        phasesFromTasks,
    };
}

export default useScheduleController;
